import { Injectable } from '@angular/core';
import { catchError, map, Observable, of, shareReplay, tap } from 'rxjs';
import { ActiveScope, ManagerScope } from 'src/app/modules/framework/home-pages/manager-hr-dashboard/models/TeamMemberRequest';
import { Student, StudentForGroup } from 'src/app/modules/lms/learningpaths/models/interfaces';
import { Employee } from 'src/app/shared/personnel-files/models/Employee';
import { IDataUrlParam } from '../../framework/images/models/DataUrlParam';
import { EmployeeTeam } from '../models/EmployeeTeam';
import { EndPointService } from '../../../core/services/end-point.service';
import { EmployeeReportFilter } from '../models/EmployeeReportFilter';
import { HttpClient } from '@angular/common/http';
import { EmployeeStatus } from '../enums/EmployeeStatus';

@Injectable({
  providedIn: 'root'
})
export class EmployeeService {
  private _baseUrl = '/api/employee/';
  private _picture = 'picture';
  private _allTeams = 'allteams';
  private _current = 'current';
  private _urlServicedesk = 'urlservicedesk';
  private readonly pictureCache = new Map<string, Observable<IDataUrlParam>>();
  private readonly missingPictureKeys = new Set<string>();
  private readonly pictureCacheMaxSize = 200;

  constructor(private _endPointService: EndPointService, private http: HttpClient) {
    this.registerEndPoints();
  }

  // #region general
  public getEmployeeStatusClass(status: EmployeeStatus) {
    switch (status) {
      case EmployeeStatus.Active: return '#28A745';
      case EmployeeStatus.Inactive: return '#bfbfbf';
      case EmployeeStatus.Locked: return 'red';
      default: return '#28A745'
    }
  }

  public getEmployeeStatusName(status: EmployeeStatus) {
    switch (status) {
      case EmployeeStatus.Active: return 'active';
      case EmployeeStatus.Inactive: return 'inactive';
      case EmployeeStatus.Locked: return 'status_locked';
      default: return 'active'
    }
  }
  // #endregion general

  private registerEndPoints(): void {
    this._endPointService.registerEndPoint<EmployeeTeam[]>(
      'employees_teams_all_get',
      `${this._baseUrl}${this._allTeams}`,
      true
    );
    this._endPointService.registerEndPoint<EmployeeTeam[]>(
      'employees_all_get',
      `${this._baseUrl}`,
      true
    );
    this._endPointService.registerEndPoint<IDataUrlParam>(
      'employee_picture',
      `${this._baseUrl}${this._picture}`,
      true
    );
    this._endPointService.registerEndPoint<Student>(
      'current',
      `${this._baseUrl}${this._current}`,
      true
    );
    this._endPointService.registerEndPoint<Student[]>(
      'reports',
      `${this._baseUrl}reports`,
      true
    );
    this._endPointService.registerEndPoint<Employee>(
      'employee_byid',
      `${this._baseUrl}byid`,
      true
    );
    this._endPointService.registerEndPoint<Employee>(
      'employee_byid_lean',
      `${this._baseUrl}byidlean`,
      false
    );


    this._endPointService.registerEndPoint<string>('urlservicedesk', `${this._baseUrl}${this._urlServicedesk}`, true);
  }

  public getEmployee(id: number, fetch?: boolean): Observable<Employee> {
    if (id == 0) {
      return of(null);
    }
    return this._endPointService.call<Employee>(
      'employee_byid',
      id,
      fetch
    );
  }

  public getEmployeeLean(id: number): Observable<Employee> {
    return this._endPointService.call<Employee>(
      'employee_byid_lean',
      id
    );
  }

  public canAccessEmployee(id: number): Observable<boolean> {
    if (!id) {
      return of(false);
    }

    return this.http.post<Employee>(`${this._baseUrl}byidlean`, id).pipe(
      map(employee => !!employee),
      catchError(() => of(false))
    );
  }

  public getAllEmployeeTeams(fetch?: boolean): Observable<EmployeeTeam[]> {
    return this._endPointService.call<EmployeeTeam[]>(
      'employees_teams_all_get',
      null,
      fetch
    );
  }
  // FA voor organogram, voor performance en klanten met veel employees gevraagd om dat niet te gebruiken (is wel gebruikt als chr)
  public getAllEmployees(fetch?: boolean): Observable<Student[]> {
    return this._endPointService.call<Student[]>(
      'employees_all_get',
      null,
      fetch
    );
  }


  public getUrlServicedesk(fetch?: boolean): Observable<string> {
    return this._endPointService.call<any>(
      'urlservicedesk',
      fetch
    ).pipe(map((e) => e.url));
  }

  public getEmployeePicture(
    whoId?: number,
    whoKind?: number,
    fetch?: boolean
  ): Observable<IDataUrlParam> {
    if (!whoId || !whoKind) {
      return of(null);
    }

    const cacheKey = this.getPictureCacheKey(whoId, whoKind);

    if (this.missingPictureKeys.has(cacheKey)) {
      return of(null);
    }

    const cachedPicture = this.pictureCache.get(cacheKey);

    if (cachedPicture) {
      // Refresh insertion order so frequently used pictures remain in the bounded cache.
      this.pictureCache.delete(cacheKey);
      this.pictureCache.set(cacheKey, cachedPicture);
      return cachedPicture;
    }

    const call = this._endPointService.call<IDataUrlParam>(
      'employee_picture',
      { whoId, whoKind },
      fetch
    ).pipe(
      tap(picture => {
        if (!picture?.fileData) {
          this.missingPictureKeys.add(cacheKey);
        }
      }),
      catchError(() => {
        // A missing picture is a valid fallback state. Remember it for this application
        // session so destroyed/recreated rows do not keep retrying the endpoint.
        this.missingPictureKeys.add(cacheKey);
        return of(null);
      }),
      // The endpoint observable is cold. Sharing it prevents duplicate HTTP calls while
      // several profile-picture components request the same person concurrently.
      shareReplay({ bufferSize: 1, refCount: false })
    );

    this.pictureCache.set(cacheKey, call);
    this.trimPictureCache();

    return call;
  }

  public invalidateEmployeePicture(whoId?: number, whoKind?: number): void {
    if (!whoId || !whoKind) {
      return;
    }

    const cacheKey = this.getPictureCacheKey(whoId, whoKind);
    this.pictureCache.delete(cacheKey);
    this.missingPictureKeys.delete(cacheKey);
  }

  private getPictureCacheKey(whoId: number, whoKind: number): string {
    return `${whoKind}:${whoId}`;
  }

  private trimPictureCache(): void {
    while (this.pictureCache.size > this.pictureCacheMaxSize) {
      const oldestKey = this.pictureCache.keys().next().value;

      if (oldestKey === undefined) {
        return;
      }

      this.pictureCache.delete(oldestKey);
    }
  }

  public getCurrentEmployee(fetch?: boolean): Observable<Student> {
    return this._endPointService.call<Student>('current', null, fetch);
  }

  public getReports(scope: ManagerScope, activeScope: ActiveScope, delegateStudentId?: number, filter?: EmployeeReportFilter, fetch?: boolean): Observable<Student[]> {
    return this._endPointService.call<Student[]>('reports', { scope, activeScope, delegateStudentId, filter: filter }, fetch);
  }

  public getReportCount(scope: ManagerScope, activeScope: ActiveScope, delegateStudentId?: number, filter?: EmployeeReportFilter): Observable<number> {
    return this.http.post<number>(`${this._baseUrl}reports/count`, { scope, activeScope, delegateStudentId, filter: filter });
  }

  public getEmployeesForGroups(): Observable<StudentForGroup[]> {
    return this.http.post<StudentForGroup[]>('/api/employee/get-employees-for-group', null);
  }
}
